import { useCallback, useEffect, useState } from "react"
import { useQuery } from "urql"

import type { ShipToLocationWithWarehouse } from "../types"

import type {
  CompanyListSuccess,
  PurchaseOrderCompanyShipToLocationListQuery,
  PurchaseOrderCompanyShipToLocationListQueryVariables,
} from "@/graphql/codegen/graphql"
import { CompanyStatus, PurchaseOrderCompanyShipToLocationListDocument } from "@/graphql/codegen/graphql"
import { CompanyType } from "@/types/companyType"

export const LIMIT = 20
const OFFSET = 0

export const useGetPurchaseOrderShipToLocation = (): {
  shipToLocations: ShipToLocationWithWarehouse[]
  shipToLocationListFetching: boolean
  loadMore: () => void
} => {
  const [offset, setOffset] = useState(OFFSET)
  const [paused, setPaused] = useState(false)
  const [shipToLocations, setShipToLocations] = useState<ShipToLocationWithWarehouse[]>([])

  const [{ data: shipToCompanyList, fetching: shipToLocationListFetching }] = useQuery<
    PurchaseOrderCompanyShipToLocationListQuery,
    PurchaseOrderCompanyShipToLocationListQueryVariables
  >({
    query: PurchaseOrderCompanyShipToLocationListDocument,
    variables: {
      input: {
        filter: {
          types: [CompanyType.AkrochemWarehouse, CompanyType.ConsignmentWarehouse],
          statuses: [CompanyStatus.Active],
        },
        limit: LIMIT,
        offset,
      },
    },
    pause: paused,
  })

  const loadMore = useCallback(() => {
    setPaused(false)
    setOffset((prevOffset) => prevOffset + LIMIT)
  }, [])

  useEffect(() => {
    const companyList = (shipToCompanyList?.company.list as CompanyListSuccess)?.companies || []

    if (!companyList.length && offset > 0) {
      setPaused(true)
      return
    }

    const newShippingLocations = companyList.flatMap((company) =>
      (company.locationsAssociations ?? []).flatMap((locationAssociation) => {
        const location = locationAssociation.shipping
        if (!location) return []

        return (location.warehouseAreas ?? [])
          .filter((warehouse) => warehouse.isActive)
          .map(
            (warehouse) =>
              ({
                ...location,
                locationAssociationId: locationAssociation.companyLocationAssociationId,
                warehouseAreaId: warehouse.warehouseAreaId,
                warehouseExternalSystemId: warehouse.externalSystemId ?? "",
              }) as ShipToLocationWithWarehouse
          )
      })
    )

    setShipToLocations((prev) => {
      const combinedLocations = offset === 0 ? newShippingLocations : [...prev, ...newShippingLocations]
      const uniqueShipToLocationsByWarehouse = new Map(
        combinedLocations.map((location) => [`${location.locationAssociationId}-${location.warehouseAreaId}`, location])
      )

      return Array.from(uniqueShipToLocationsByWarehouse.values()).sort((a, b) =>
        a.warehouseExternalSystemId.localeCompare(b.warehouseExternalSystemId, undefined, { numeric: true })
      )
    })
  }, [shipToCompanyList, offset])

  return {
    shipToLocations,
    shipToLocationListFetching,
    loadMore,
  }
}
